import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  TextField,
  Chip,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useModules } from '../../hooks/subscriptions/useModules';
import { useEnableModule, useOrgModules } from '../../hooks/subscriptions/useOrgModules';
import { useValidateEnableModule } from '../../hooks/subscriptions/useModuleDependencies';
import { useSnackbar } from '../SnackbarProvider';
import { getCategoryLabel, getCategoryColor } from '../../utils/subscriptions';
import { DependencyResolutionDialog } from './DependencyResolutionDialog';
import type { ValidateEnableModuleResponse } from '../../types/subscriptions';

interface EnableModuleDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
}

export const EnableModuleDialog = ({
  open,
  onClose,
  organizationId,
  organizationName,
}: EnableModuleDialogProps) => {
  const { showMessage } = useSnackbar();
  const [selectedModuleKey, setSelectedModuleKey] = useState('');
  const [isTrial, setIsTrial] = useState(false);
  const [trialDays, setTrialDays] = useState(14);
  const [autoResolveDependencies, setAutoResolveDependencies] = useState(true);

  // Dependency resolution dialog state
  const [showDependencyDialog, setShowDependencyDialog] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidateEnableModuleResponse | null>(null);

  const { data: allModules, isLoading: modulesLoading } = useModules();
  const { data: orgModules } = useOrgModules(organizationId);
  const enableMutation = useEnableModule(organizationId);
  const validateMutation = useValidateEnableModule();

  // Get modules that are not currently enabled
  const enabledModuleIds = new Set(
    orgModules?.filter(om => !om.disabledAt).map(om => om.module?.key) || []
  );

  const availableModules = allModules?.filter(
    module => !enabledModuleIds.has(module.key)
  );

  const selectedModule = allModules?.find(m => m.key === selectedModuleKey);

  // Validate module when selected
  useEffect(() => {
    if (selectedModuleKey && organizationId) {
      validateMutation.mutate(selectedModuleKey, {
        onSuccess: (result) => {
          setValidationResult(result);
          // If there are missing dependencies, show the dialog
          if (!result.valid && result.missingDependencies.length > 0) {
            setShowDependencyDialog(true);
          }
        },
        onError: (error: any) => {
          console.error('Validation failed:', error);
          showMessage(
            error.response?.data?.message || 'Failed to validate module',
            'error'
          );
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModuleKey, organizationId]);

  const handleDependencyDialogConfirm = (autoResolve: boolean) => {
    setAutoResolveDependencies(autoResolve);
    setShowDependencyDialog(false);
  };

  const handleEnable = () => {
    if (!selectedModuleKey) {
      showMessage('Please select a module', 'warning');
      return;
    }

    if (isTrial && (!trialDays || trialDays < 1)) {
      showMessage('Trial days must be at least 1', 'warning');
      return;
    }

    enableMutation.mutate(
      {
        moduleKey: selectedModuleKey,
        isTrial,
        trialDays: isTrial ? trialDays : undefined,
        autoResolveDependencies,
      },
      {
        onSuccess: (response) => {
          // Count auto-enabled modules
          const autoEnabledCount = response?.autoEnabledModules?.length || 0;

          let message = `Module ${isTrial ? 'trial started' : 'enabled'} successfully`;
          if (autoEnabledCount > 0) {
            message += ` (+ ${autoEnabledCount} ${autoEnabledCount === 1 ? 'dependency' : 'dependencies'} auto-enabled)`;
          }

          showMessage(message, 'success');
          handleClose();
        },
        onError: (error: any) => {
          showMessage(
            error.response?.data?.message || 'Failed to enable module',
            'error'
          );
        },
      }
    );
  };

  const handleClose = () => {
    if (!enableMutation.isPending) {
      onClose();
      setSelectedModuleKey('');
      setIsTrial(false);
      setTrialDays(14);
      setAutoResolveDependencies(true);
      setShowDependencyDialog(false);
      setValidationResult(null);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <AddIcon />
          <Typography variant="h6">Enable Module</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Enable a module for <strong>{organizationName}</strong>
          </Typography>

          {modulesLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              <FormControl fullWidth sx={{ mt: 3 }}>
                <InputLabel>Select Module</InputLabel>
                <Select
                  value={selectedModuleKey}
                  onChange={(e) => setSelectedModuleKey(e.target.value)}
                  label="Select Module"
                >
                  {availableModules?.map((module) => (
                    <MenuItem key={module.id} value={module.key}>
                      {module.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedModule && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                    <Typography variant="subtitle2">
                      {selectedModule.name}
                    </Typography>
                    {selectedModule.category && (
                      <Chip
                        label={getCategoryLabel(selectedModule.category)}
                        size="small"
                        sx={{
                          backgroundColor: getCategoryColor(selectedModule.category) + '20',
                          color: getCategoryColor(selectedModule.category),
                          borderColor: getCategoryColor(selectedModule.category),
                          borderWidth: 1,
                          borderStyle: 'solid',
                        }}
                      />
                    )}
                    {selectedModule.isCore && (
                      <Chip
                        label="Core"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Stack>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    {selectedModule.description}
                  </Typography>

                  {selectedModule.dependencies && selectedModule.dependencies.length > 0 && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2" gutterBottom>
                        <strong>Dependencies:</strong>
                      </Typography>
                      <Typography variant="body2">
                        This module requires: {selectedModule.dependencies.join(', ')}
                      </Typography>
                    </Alert>
                  )}
                </Box>
              )}

              <Box sx={{ mt: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isTrial}
                      onChange={(e) => setIsTrial(e.target.checked)}
                    />
                  }
                  label="Enable as trial"
                />
              </Box>

              {isTrial && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1 }}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Trial Duration (days)"
                    value={trialDays}
                    onChange={(e) => setTrialDays(Number(e.target.value))}
                    inputProps={{ min: 1, max: 90 }}
                    helperText="Number of days before trial expires (1-90)"
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    The module will be automatically disabled when the trial period ends.
                  </Typography>
                </Box>
              )}

              {availableModules?.length === 0 && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  All available modules are already enabled for this organization.
                </Alert>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={enableMutation.isPending}>
          Cancel
        </Button>
        <Button
          onClick={handleEnable}
          variant="contained"
          disabled={!selectedModuleKey || enableMutation.isPending}
        >
          {enableMutation.isPending ? 'Enabling...' : isTrial ? 'Start Trial' : 'Enable Module'}
        </Button>
      </DialogActions>
    </Dialog>

      {/* Dependency Resolution Dialog */}
      {validationResult && selectedModule && (
        <DependencyResolutionDialog
          open={showDependencyDialog}
          onClose={() => setShowDependencyDialog(false)}
          module={{
            id: selectedModule.id,
            key: selectedModule.key,
            name: selectedModule.name,
            description: selectedModule.description,
            category: selectedModule.category,
            isCore: selectedModule.isCore,
          }}
          missingDependencies={validationResult.missingDependencies}
          willAutoEnable={validationResult.willAutoEnable}
          onConfirm={handleDependencyDialogConfirm}
          isLoading={enableMutation.isPending}
        />
      )}
    </>
  );
};
